import { useEffect, useRef } from 'react';
import type { OperationContext } from 'urql';
import { useSetAtom } from '~/atom';
import {
  backgroundOperationsAtom,
  type BackgroundOperation,
} from './backgroundOpsAtom';
import { networkOnlyWithSignal } from './urqlHelpers';

export type UpdateEntityFn<TInput> = (
  variables: { id: string; input: TInput },
  context?: Partial<OperationContext>
) => Promise<unknown>;

const UPDATE_CONCURRENCY_LIMIT = 50;

const normalizeType = (v: unknown) =>
  (v == null ? '' : String(v)).trim().toLowerCase();

// Check if an entity has a specific type (supports both string and array fields)
const entityHasType = <T>(
  getType: (entity: T) => string | string[] | null | undefined,
  entity: T,
  targetType: string
): boolean => {
  const typeValue = getType(entity);
  if (Array.isArray(typeValue)) {
    return typeValue.some(
      (t) => normalizeType(t) === normalizeType(targetType)
    );
  }
  return normalizeType(typeValue) === normalizeType(targetType);
};

type EnrichedError = Error & {
  cause?: unknown;
  opId: string;
  failedIds: string[];
  errorMessages: string[];
  failedNames?: string[];
};

const processEntityBatch = async <
  UEntity extends { id: string },
  UInput
>(config: {
  entities: UEntity[];
  from: string;
  to: string;
  buildInput: (entity: UEntity, from: string, to: string) => UInput;
  updateEntity: UpdateEntityFn<UInput>;
  controller: AbortController;
  maxRetries: number;
  onProgress: (count: number) => void;
  onBatchComplete: (failedIds: string[], errorMessages: string[]) => void;
}) => {
  const extractErrorMessages = (err: unknown): string[] => {
    const messages: string[] = [];
    const isObject = (v: unknown): v is Record<string, unknown> =>
      typeof v === 'object' && v !== null;
    const hasString = (v: unknown): v is string => typeof v === 'string';
    const isGraphQLErrorLike = (v: unknown): v is { message?: string } =>
      isObject(v) && (typeof v.message === 'string' || 'message' in v);
    const isNetworkErrorLike = (
      v: unknown
    ): v is { message?: string; status?: number } =>
      isObject(v) && ('message' in v || 'status' in v);
    const isCombinedErrorLike = (
      v: unknown
    ): v is {
      graphQLErrors?: unknown[];
      networkError?: unknown;
      message?: string;
    } =>
      isObject(v) &&
      ('graphQLErrors' in v || 'networkError' in v || 'message' in v);

    if (isCombinedErrorLike(err)) {
      if (Array.isArray(err.graphQLErrors)) {
        for (const ge of err.graphQLErrors) {
          if (
            isGraphQLErrorLike(ge) &&
            hasString(ge.message) &&
            ge.message.trim()
          ) {
            messages.push(ge.message.trim());
          }
        }
      }
      if (isNetworkErrorLike(err.networkError)) {
        if (
          hasString(err.networkError.message) &&
          err.networkError.message.trim()
        ) {
          messages.push(err.networkError.message.trim());
        }
        if (typeof err.networkError.status === 'number') {
          messages.push(`HTTP ${err.networkError.status}`);
        }
      }
      if (hasString(err.message) && err.message.trim()) {
        messages.push(err.message.trim());
      }
    }
    if (messages.length === 0) {
      const fallback =
        isObject(err) &&
        'message' in err &&
        hasString((err as { message?: string }).message)
          ? (err as { message?: string }).message || 'Unknown error'
          : String(err ?? 'Unknown error');
      messages.push(fallback);
    }
    return Array.from(new Set(messages));
  };
  const {
    entities,
    buildInput,
    updateEntity,
    controller,
    maxRetries,
    onProgress,
    onBatchComplete,
  } = config;
  for (let i = 0; i < entities.length; i += UPDATE_CONCURRENCY_LIMIT) {
    if (controller.signal.aborted) throw new Error('Operation cancelled');
    const batch = entities.slice(i, i + UPDATE_CONCURRENCY_LIMIT);
    const doOne = async (entity: UEntity) => {
      let attempt = 0;
      while (true) {
        try {
          const input = buildInput(entity, config.from, config.to);
          console.log(
            `[useBackgroundTypeUpdater] Update entity ${entity.id} with input:`,
            JSON.stringify(input, null, 2)
          );
          const result = await updateEntity(
            { id: entity.id, input },
            networkOnlyWithSignal(controller.signal)
          );
          if (result && typeof result === 'object') {
            const maybe = result as { error?: unknown };
            const error = maybe.error as unknown;
            if (error) {
              console.error(
                `[useBackgroundTypeUpdater] Mutation returned error for entity ${entity.id}:`,
                error
              );
              console.error(
                `[useBackgroundTypeUpdater] Input that caused error:`,
                JSON.stringify(input, null, 2)
              );
              // GraphQL errors should be treated as failures and thrown
              throw error;
            }
          }
          return result;
        } catch (e) {
          console.error(
            `[useBackgroundTypeUpdater] Failed to update entity ${
              entity.id
            } (attempt ${attempt + 1}/${maxRetries + 1}):`,
            e
          );
          console.error(
            `[useBackgroundTypeUpdater] Entity data:`,
            JSON.stringify(entity, null, 2)
          );
          if (attempt >= maxRetries) throw e;
          await new Promise((r) => setTimeout(r, 200 * Math.pow(2, attempt)));
          attempt += 1;
        }
      }
    };
    const results = await Promise.allSettled(batch.map((e) => doOne(e)));
    onProgress(batch.length);
    const batchFailedIds: string[] = [];
    const batchErrorMessages: string[] = [];
    for (let idx = 0; idx < results.length; idx++) {
      const r = results[idx];
      if (r.status !== 'fulfilled') {
        const failedId = batch[idx]?.id;
        if (failedId != null) batchFailedIds.push(failedId);
        const err = (r as PromiseRejectedResult).reason;
        const msgs = extractErrorMessages(err);
        if (msgs.length) batchErrorMessages.push(...msgs);
      }
    }
    if (batchFailedIds.length > 0) {
      console.error(
        `[useBackgroundTypeUpdater] Batch had ${batchFailedIds.length} failures:`,
        batchFailedIds,
        batchErrorMessages
      );
    }
    onBatchComplete(batchFailedIds, batchErrorMessages);
  }
};

export const useBackgroundTypeUpdater = <
  TEntity extends { id: string },
  TInput
>(params: {
  getType: (entity: TEntity) => string | string[] | null | undefined;
  getName?: (entity: TEntity) => string | null | undefined;
}) => {
  const getType = params.getType;
  const getName = params.getName;
  const controllerRef = useRef<AbortController | null>(null);
  const runningRef = useRef<boolean>(false);
  const setOps = useSetAtom(backgroundOperationsAtom);

  // ensureController removed; each run creates its own controller

  const cancel = () => {
    if (controllerRef.current && !controllerRef.current.signal.aborted) {
      controllerRef.current.abort();
    }
    // Always clear ref so subsequent runs create a fresh controller
    controllerRef.current = null;
  };

  const buildEnrichedError = (
    baseError: unknown,
    context: {
      opId: string;
      label: string;
      failedIds: string[];
      errorMessages: string[];
      idToName?: Map<string, string>;
    }
  ) => {
    const originalMsg =
      baseError instanceof Error
        ? baseError.message
        : String(baseError ?? 'Unknown error');
    const failedCount = context.failedIds.length;
    const sampleIds =
      failedCount > 0 ? context.failedIds.slice(0, 5).join(', ') : '';
    const failedNames: string[] = [];
    if (context.idToName && failedCount > 0) {
      for (const id of context.failedIds.slice(0, 5)) {
        const nm = context.idToName.get(id);
        if (nm && nm.trim().length > 0) failedNames.push(nm);
      }
    }
    const sampleNames = failedNames.length > 0 ? failedNames.join(', ') : '';
    const uniqueMsgs = Array.from(new Set(context.errorMessages)).slice(0, 3);
    const details = [
      failedCount > 0
        ? `${failedCount} failures${
            sampleNames
              ? ` (e.g., ${sampleNames})`
              : sampleIds
              ? ` (ids: ${sampleIds})`
              : ''
          }`
        : undefined,
      uniqueMsgs.length > 0 ? `errors: ${uniqueMsgs.join(' | ')}` : undefined,
    ]
      .filter(Boolean)
      .join('; ');
    const message = details
      ? `${context.label} failed: ${details}. Cause: ${originalMsg}`
      : `${context.label} failed: ${originalMsg}`;
    const enriched: EnrichedError = Object.assign(new Error(message), {
      cause: baseError,
      opId: context.opId,
      failedIds: context.failedIds,
      errorMessages: context.errorMessages,
      failedNames,
    });
    return enriched;
  };

  const runRenameUpdates = async (args: {
    from: string;
    to: string;
    allEntities: TEntity[];
    buildInput: (entity: TEntity, from: string, to: string) => TInput;
    updateEntity: UpdateEntityFn<TInput>;
    progress: {
      start: (opts: {
        label: string;
        delayMs?: number;
        persistent?: boolean;
      }) => void;
      setTotal: (n: number) => void;
      increment: (n?: number) => void;
      done: (msg?: string) => void;
      error: (msg?: string) => void;
      cancel: () => void;
    };
    label?: string;
    opId?: string;
    retries?: number;
    managedProgress?: boolean; // if true, caller handles start/setTotal; we only increment
    completionMessage?: string; // when provided and managedProgress is true, we will call done
    failureMessage?: string; // when provided and managedProgress is true, we will call error
  }) => {
    // Prevent concurrent runs
    if (runningRef.current) return;
    const { from, to, allEntities, buildInput, updateEntity, progress } = args;
    const label = args.label ?? 'Updating…';
    const opId = args.opId ?? `rename-${from}-${to}-${Date.now()}`;
    const maxRetries = Math.max(0, args.retries ?? 2);
    const managedProgress = !!args.managedProgress;
    const toUpdate = (allEntities || []).filter((e) =>
      entityHasType(getType, e, from)
    );
    if (toUpdate.length === 0) {
      if (!managedProgress) {
        // Only cancel local progress if we own it
        try {
          progress.cancel();
        } catch {
          /* ignore cancellation errors */
        }
      } else {
        // For managed progress, complete successfully since there's nothing to do
        if (args.completionMessage) progress.done(args.completionMessage);
      }
      return;
    }

    // fresh controller per run to isolate cancellation and cleanup
    controllerRef.current = new AbortController();
    const controller = controllerRef.current;
    runningRef.current = true;
    if (!managedProgress) {
      progress.start({ label, delayMs: 300, persistent: true });
      progress.setTotal(toUpdate.length);
    }

    let anyFailed = false;
    const failedIds: string[] = [];
    const errorMessages: string[] = [];
    const startedAt = Date.now();
    setOps((prev) => [
      ...prev,
      {
        id: opId,
        kind: 'rename',
        label,
        status: 'running',
        total: toUpdate.length,
        completed: 0,
        failedIds: [],
        errorMessages: [],
        startedAt,
        cancel,
      } as BackgroundOperation,
    ]);

    // map ids to names for richer error messages
    const idToName: Map<string, string> | undefined = getName
      ? new Map(
          (allEntities || [])
            .filter((e) => entityHasType(getType, e, from))
            .map((e) => [e.id, (getName(e) ?? '').toString()])
        )
      : undefined;

    try {
      await processEntityBatch<TEntity, TInput>({
        entities: toUpdate,
        from,
        to,
        buildInput,
        updateEntity,
        controller,
        maxRetries,
        onProgress: (count) => {
          progress.increment(count);
          setOps((prev) =>
            prev.map((op) =>
              op.id === opId
                ? { ...op, completed: Math.min(op.total, op.completed + count) }
                : op
            )
          );
        },
        onBatchComplete: (batchFailedIds, batchErrorMessages) => {
          if (batchFailedIds.length > 0) anyFailed = true;
          if (batchFailedIds.length) failedIds.push(...batchFailedIds);
          if (batchErrorMessages.length)
            errorMessages.push(...batchErrorMessages);
          setOps((prev) =>
            prev.map((op) =>
              op.id === opId
                ? {
                    ...op,
                    failedIds,
                    errorMessages,
                  }
                : op
            )
          );
        },
      });
      if (anyFailed) {
        const enriched = buildEnrichedError(new Error('Some updates failed'), {
          opId,
          label,
          failedIds,
          errorMessages,
          idToName,
        });
        if (!managedProgress) {
          progress.error(enriched.message);
        } else {
          // For managed progress, show error toast and propagate
          if (args.failureMessage) progress.error(args.failureMessage);
        }
        setOps((prev) =>
          prev.map((op) =>
            op.id === opId
              ? {
                  ...op,
                  status: 'error',
                  finishedAt: Date.now(),
                  failedIds,
                  errorMessages,
                }
              : op
          )
        );
        if (managedProgress) {
          // For managed progress flows, propagate a detailed error so callers can surface it
          throw enriched;
        }
      } else {
        if (managedProgress) {
          // Show success toast with auto-dismiss
          if (args.completionMessage) progress.done(args.completionMessage);
        }
        setOps((prev) =>
          prev.map((op) =>
            op.id === opId
              ? { ...op, status: 'success', finishedAt: Date.now() }
              : op
          )
        );
      }
    } catch (e) {
      if ((e as Error)?.message === 'Operation cancelled') {
        if (!managedProgress) progress.cancel();
        setOps((prev) =>
          prev.map((op) =>
            op.id === opId
              ? { ...op, status: 'cancelled', finishedAt: Date.now() }
              : op
          )
        );
        throw e;
      } else {
        const enriched = buildEnrichedError(e, {
          opId,
          label,
          failedIds,
          errorMessages,
          idToName,
        });
        if (!managedProgress) {
          progress.error(enriched.message);
        } else {
          // For managed progress, show error toast
          if (args.failureMessage) progress.error(args.failureMessage);
        }
        setOps((prev) =>
          prev.map((op) =>
            op.id === opId
              ? {
                  ...op,
                  status: 'error',
                  finishedAt: Date.now(),
                  failedIds,
                  errorMessages,
                }
              : op
          )
        );
        throw enriched;
      }
    } finally {
      runningRef.current = false;
      // Clear controller after completion for a clean next run
      controllerRef.current = null;
    }
  };

  const runBulkReassignUpdates = async (args: {
    tasks: Array<{ from: string; to: string }>;
    allEntities: TEntity[];
    buildInput: (entity: TEntity, from: string, to: string) => TInput;
    updateEntity: UpdateEntityFn<TInput>;
    progress: {
      start: (opts: {
        label: string;
        delayMs?: number;
        persistent?: boolean;
      }) => void;
      setTotal: (n: number) => void;
      increment: (n?: number) => void;
      done: (msg?: string) => void;
      error: (msg?: string) => void;
      cancel: () => void;
    };
    label?: string;
    opId?: string;
    retries?: number;
    managedProgress?: boolean; // if true, caller manages start/setTotal/done/error/cancel
    completionMessage?: string; // used only when managedProgress is false
    failureMessage?: string; // used only when managedProgress is false
  }) => {
    // Prevent concurrent runs
    if (runningRef.current) return;
    const { tasks, allEntities, buildInput, updateEntity, progress } = args;
    const label = args.label ?? 'Updating…';
    const opId = args.opId ?? `bulk-${Date.now()}`;
    const maxRetries = Math.max(0, args.retries ?? 2);
    const managedProgress = !!args.managedProgress;

    // Calculate total before setting up controller
    let total = 0;
    for (const { from } of tasks)
      total += (allEntities || []).filter((e) =>
        entityHasType(getType, e, from)
      ).length;

    if (total === 0) {
      // No entities to update; complete immediately without setting up controller
      if (!managedProgress) {
        try {
          progress.cancel();
        } catch {
          /* ignore cancellation errors */
        }
      } else {
        // For managed progress, complete successfully since there's nothing to do
        if (args.completionMessage) progress.done(args.completionMessage);
      }
      return;
    }

    // fresh controller per run to isolate cancellation and cleanup
    controllerRef.current = new AbortController();
    const controller = controllerRef.current;
    runningRef.current = true;

    if (!managedProgress) {
      progress.start({ label, delayMs: 300, persistent: true });
      if (total > 0) progress.setTotal(total);
    }

    let anyFailed = false;
    const failedIds: string[] = [];
    const errorMessages: string[] = [];
    const startedAt = Date.now();
    setOps((prev) => [
      ...prev,
      {
        id: opId,
        kind: 'bulkReassign',
        label,
        status: 'running',
        total,
        completed: 0,
        failedIds: [],
        errorMessages: [],
        startedAt,
        cancel,
      } as BackgroundOperation,
    ]);

    // id->name map for all entities across tasks (broader scope)
    const idToName: Map<string, string> | undefined = getName
      ? new Map(
          (allEntities || []).map((e) => [e.id, (getName(e) ?? '').toString()])
        )
      : undefined;

    try {
      for (const { from, to } of tasks) {
        const entities = (allEntities || []).filter((e) =>
          entityHasType(getType, e, from)
        );
        await processEntityBatch<TEntity, TInput>({
          entities,
          from,
          to,
          buildInput,
          updateEntity,
          controller,
          maxRetries,
          onProgress: (count) => {
            progress.increment(count);
            setOps((prev) =>
              prev.map((op) =>
                op.id === opId
                  ? {
                      ...op,
                      completed: Math.min(op.total, op.completed + count),
                    }
                  : op
              )
            );
          },
          onBatchComplete: (batchFailedIds, batchErrorMessages) => {
            if (batchFailedIds.length > 0) anyFailed = true;
            if (batchFailedIds.length) failedIds.push(...batchFailedIds);
            if (batchErrorMessages.length)
              errorMessages.push(...batchErrorMessages);
            setOps((prev) =>
              prev.map((op) =>
                op.id === opId
                  ? {
                      ...op,
                      failedIds,
                      errorMessages,
                    }
                  : op
              )
            );
          },
        });
      }
      if (anyFailed) {
        const enriched = buildEnrichedError(
          new Error(args.failureMessage || 'Some updates failed'),
          {
            opId,
            label,
            failedIds,
            errorMessages,
            idToName,
          }
        );
        if (!managedProgress) {
          progress.error(enriched.message);
        } else {
          // For managed progress, show error toast and propagate
          if (args.failureMessage) progress.error(args.failureMessage);
        }
        setOps((prev) =>
          prev.map((op) =>
            op.id === opId
              ? {
                  ...op,
                  status: 'error',
                  finishedAt: Date.now(),
                  failedIds,
                  errorMessages,
                }
              : op
          )
        );
        if (managedProgress) {
          // For managed flows, throw so caller can render a specific error toast
          throw enriched;
        }
      } else {
        if (managedProgress) {
          // Show success toast with auto-dismiss
          if (args.completionMessage) progress.done(args.completionMessage);
        }
        setOps((prev) =>
          prev.map((op) =>
            op.id === opId
              ? { ...op, status: 'success', finishedAt: Date.now() }
              : op
          )
        );
      }
    } catch (e) {
      if ((e as Error)?.message === 'Operation cancelled') {
        if (!managedProgress) progress.cancel();
        setOps((prev) =>
          prev.map((op) =>
            op.id === opId
              ? { ...op, status: 'cancelled', finishedAt: Date.now() }
              : op
          )
        );
        throw e;
      } else {
        const enriched = buildEnrichedError(e, {
          opId,
          label,
          failedIds,
          errorMessages,
          idToName,
        });
        if (!managedProgress) {
          progress.error(enriched.message);
        } else {
          // For managed progress, show error toast
          if (args.failureMessage) progress.error(args.failureMessage);
        }
        setOps((prev) =>
          prev.map((op) =>
            op.id === opId
              ? {
                  ...op,
                  status: 'error',
                  finishedAt: Date.now(),
                  failedIds,
                  errorMessages,
                }
              : op
          )
        );
        throw enriched;
      }
    } finally {
      runningRef.current = false;
      // Clear controller after completion for a clean next run
      controllerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      // Do not auto-cancel on unmount so background ops can continue after dialogs close
      controllerRef.current = null;
    };
  }, []);

  return { runRenameUpdates, runBulkReassignUpdates, cancel, controllerRef };
};
